import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useIsMutating } from '@tanstack/react-query';

import { useDeleteInscriptionOneClick } from '@/hooks/useTransbank';
import { useDelOneClickUser, useGetOneClickCards } from '@/hooks/useAuth';
import { useAuth } from '@/_zustand/auth';
import { filtrarCredencialesParaBody } from '@/lib/transbankCredenciales';

/**
 * Hook unificado para eliminar tarjetas OneClick.
 * Maneja correctamente el flujo para usuarios invitados y registrados,
 * y envía los parámetros con la nomenclatura correcta hacia la API.
 */
export const useDeleteOneClickCard = () => {
    const isDevelopment = import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.VITE_APP_ENV === 'testing';
    const { user, authType, setTempCard } = useAuth();
    const deleteInscription = useDeleteInscriptionOneClick();
    const deleteOneClickUser = useDelOneClickUser();
    const getOneClickCards = useGetOneClickCards();

    const deletingInscription = useIsMutating({ mutationKey: ['useDeleteInscriptionOneClick'] });
    const deletingUser = useIsMutating({ mutationKey: ['useDelOneClickUser'] });
    const isDeleting = deletingInscription > 0 || deletingUser > 0;

    /**
     * Elimina una tarjeta de Transbank y de la BD (si es usuario registrado).
     * Equivalente al flujo de Sima-appmovil: onGetOnePay() → inst.delete() → deleteOneClick()
     * @param {object} card - Tarjeta a eliminar (debe tener tbk_user, codigo_comercio)
     */
    const deleteCard = async (card) => {
        // Obtener credenciales desde la API (equivalente a onGetOnePay() en mobile)
        let codeComerce = import.meta.env.VITE_CODE_COMERCE_DEV;
        let apiKey = import.meta.env.VITE_API_KEY_DEV;

        if (!isDevelopment) {
            const configs = await getOneClickCards.mutateAsync();
            const config = Array.isArray(configs)
                ? (configs.find(c => c.oneclick_codigo_comercio === card.codigo_comercio) ?? configs[0])
                : null;

            if (!config) throw new Error('No se encontró configuración OneClick para esta tarjeta');

            codeComerce = config.oneclick_codigo_comercio;
            apiKey = config.oneclick_apikey;
        }

        const transbankPayload = {
            token: card.tbk_user,
            email: user.usuario_correo,
            ...filtrarCredencialesParaBody({ codeComerce, apiKey }),
        };

        if (authType === 'invitado') {
            await deleteInscription.mutateAsync(transbankPayload);
        } else {
            // Para usuarios registrados: primero eliminar de la BD, luego de Transbank
            await deleteOneClickUser.mutateAsync({
                usuario_id: user.usuario_id,
                codigo_comercio: card.codigo_comercio,
                tbk_card: card.tbk_user,
            });
            await deleteInscription.mutateAsync(transbankPayload);
        }

        setTempCard({});
    };

    /**
     * Muestra confirmación Swal y elimina la tarjeta si el usuario confirma.
     * @param {object} card - Tarjeta a eliminar
     * @param {function} [onSuccess] - Callback ejecutado tras eliminación exitosa
     */
    const confirmAndDeleteCard = (card, onSuccess) => {
        Swal.fire({
            title: 'Eliminar tarjeta',
            text: '¿Está seguro de eliminar la tarjeta seleccionada?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#C62D2D',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Eliminando tarjeta...',
                    text: 'Por favor espere',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => Swal.showLoading(),
                });
                try {
                    await deleteCard(card);
                    Swal.close();
                    toast.success('Tarjeta eliminada correctamente');
                    onSuccess?.();
                } catch (error) {
                    console.error('Error eliminando tarjeta:', error);
                    Swal.close();
                    toast.error('Error al eliminar la tarjeta');
                }
            }
        });
    };

    return { deleteCard, confirmAndDeleteCard, isDeleting };
};
